import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  CloudUpload as UploadIcon,
  LocationOn as LocationIcon,
  PhotoCamera as CameraIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { checklistResponseCountsAsDone, collectionHasCannotCompleteWithoutReason } from '../../utils/checklistItemUtils';

interface ChecklistItem {
  id: number;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules: any;
  options: string[];
  default_value: string;
  allowed_file_types: string;
  max_file_size_mb: number;
  is_visible: boolean;
  show_when_parent_completed: boolean;
}

interface Checklist {
  id: number;
  title: string;
  description: string;
  category: string;
  version: number;
  is_active: boolean;
  require_all_items: boolean;
  minimum_completion_percentage: number;
  total_items: number;
  required_items: number;
  created_at: string;
  updated_at: string;
  items: ChecklistItem[];
}

interface ItemResponse {
  item_id: number;
  instance_id?: number;
  response: any;
  notes: string;
  cannot_complete: boolean;
  cannot_complete_reason: string;
  file_uploads?: File[];
}

interface ChecklistInstance {
  id: number;
  checklist: number;
  instance_name: string;
  instance_identifier: string;
  location_details: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ChecklistReviewDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  checklist: Checklist;
  responses: { [key: number]: ItemResponse };
  instances?: ChecklistInstance[];
  instanceResponses?: {[instanceId: number]: {[itemId: number]: ItemResponse}};
}

const ChecklistReviewDialog: React.FC<ChecklistReviewDialogProps> = ({
  open,
  onClose,
  onComplete,
  checklist,
  responses,
  instances,
  instanceResponses
}) => {
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  const getItemTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'text_input': 'Text Input',
      'number_input': 'Number Input',
      'checkbox': 'Checkbox',
      'file_upload': 'File Upload',
      'date_input': 'Date Input',
      'time_input': 'Time Input',
      'select': 'Dropdown',
      'multi_select': 'Multi-select',
      'radio': 'Radio Buttons',
      'signature': 'Digital Signature',
      'location': 'Location/GPS',
      'photo': 'Photo Capture'
    };
    return labels[type] || type;
  };

  const formatResponse = (item: ChecklistItem, response?: ItemResponse) => {
    if (!response) {
      return <Typography color="text.secondary" fontStyle="italic">Not answered</Typography>;
    }

    if (response.cannot_complete) {
      return (
        <Box>
          <Chip label="Cannot Complete" color="warning" size="small" sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {response.cannot_complete_reason}
          </Typography>
        </Box>
      );
    }

    switch (item.item_type) {
      case 'checkbox':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {response.response === true ? (
              <>
                <CheckIcon color="success" />
                <Typography>Checked</Typography>
              </>
            ) : (
              <>
                <WarningIcon color="disabled" />
                <Typography color="text.secondary">Not checked</Typography>
              </>
            )}
          </Box>
        );

      case 'text_input':
      case 'number_input':
      case 'date_input':
      case 'time_input':
      case 'signature':
        return (
          <Typography variant="body2">
            {response.response || 'No response'}
          </Typography>
        );

      case 'select':
      case 'radio':
        return (
          <Typography variant="body2">
            {response.response || 'No selection'}
          </Typography>
        );

      case 'file_upload':
        if (response.file_uploads && response.file_uploads.length > 0) {
          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <UploadIcon color="primary" />
                <Typography variant="body2">
                  {response.file_uploads.length} file(s) uploaded
                </Typography>
              </Box>
              {response.file_uploads.map((file, index) => (
                <Typography key={index} variant="caption" color="text.secondary">
                  • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              ))}
            </Box>
          );
        }
        return <Typography color="text.secondary">No files uploaded</Typography>;

      case 'location':
        if (response.response) {
          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationIcon color="primary" />
                <Typography variant="body2">Location captured</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Lat: {response.response.latitude}, Long: {response.response.longitude}
              </Typography>
            </Box>
          );
        }
        return <Typography color="text.secondary">No location captured</Typography>;

      case 'photo':
        if (response.file_uploads && response.file_uploads.length > 0) {
          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CameraIcon color="primary" />
                <Typography variant="body2">
                  {response.file_uploads.length} photo(s) taken
                </Typography>
              </Box>
              <Grid container spacing={1}>
                {response.file_uploads.map((file, index) => (
                  <Grid item xs={4} key={index}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Photo ${index + 1}`}
                      style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        }
        return <Typography color="text.secondary">No photos taken</Typography>;

      default:
        return (
          <Typography variant="body2">
            {JSON.stringify(response.response)}
          </Typography>
        );
    }
  };

  const getCompletionStatus = () => {
    const totalItems = checklist.items.length;
    const requiredItems = checklist.items.filter(item => item.is_required);
    const completedItems = checklist.items.filter(item => {
      const response = responses[item.id];
      return checklistResponseCountsAsDone(response);
    });
    const completedRequiredItems = requiredItems.filter(item => {
      const response = responses[item.id];
      return checklistResponseCountsAsDone(response);
    });

    return {
      total: totalItems,
      completed: completedItems.length,
      required: requiredItems.length,
      completedRequired: completedRequiredItems.length,
      percentage: Math.round((completedItems.length / totalItems) * 100),
      requiredPercentage: Math.round((completedRequiredItems.length / requiredItems.length) * 100)
    };
  };

  // Helper function to get completion status for a specific instance
  const getInstanceCompletionStatus = (instanceId: number) => {
    const instResponses = instanceResponses?.[instanceId] || {};
    const totalItems = checklist.items.length;
    const requiredItems = checklist.items.filter(item => item.is_required);
    const completedItems = checklist.items.filter(item => {
      const response = instResponses[item.id];
      return checklistResponseCountsAsDone(response);
    });
    const completedRequiredItems = requiredItems.filter(item => {
      const response = instResponses[item.id];
      return checklistResponseCountsAsDone(response);
    });

    return {
      total: totalItems,
      completed: completedItems.length,
      required: requiredItems.length,
      completedRequired: completedRequiredItems.length,
      percentage: Math.round((completedItems.length / totalItems) * 100),
      requiredPercentage: Math.round((completedRequiredItems.length / requiredItems.length) * 100)
    };
  };

  // Helper function to get overall completion status across all instances
  const getOverallCompletionStatus = () => {
    if (!instances || instances.length === 0) {
      return getCompletionStatus();
    }

    let totalCompleted = 0;
    let totalItems = 0;
    let totalRequiredCompleted = 0;
    let totalRequired = 0;

    instances.forEach(instance => {
      const status = getInstanceCompletionStatus(instance.id);
      totalCompleted += status.completed;
      totalItems += status.total;
      totalRequiredCompleted += status.completedRequired;
      totalRequired += status.required;
    });

    return {
      total: totalItems,
      completed: totalCompleted,
      required: totalRequired,
      completedRequired: totalRequiredCompleted,
      percentage: Math.round((totalCompleted / totalItems) * 100),
      requiredPercentage: Math.round((totalRequiredCompleted / totalRequired) * 100)
    };
  };

  const status = instances && instances.length > 0 ? getOverallCompletionStatus() : getCompletionStatus();
  const missingCannotCompleteReason = collectionHasCannotCompleteWithoutReason(responses)
    || (instances || []).some((instance) => collectionHasCannotCompleteWithoutReason(instanceResponses?.[instance.id]));
  const isComplete = status.completedRequired === status.required && !missingCannotCompleteReason;

  const renderTableReview = () => (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Response</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Required</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checklist.items.map((item) => {
            const response = responses[item.id];
            const isCompleted = checklistResponseCountsAsDone(response);
            
            return (
              <TableRow 
                key={item.id}
                sx={{ 
                  bgcolor: isCompleted ? 'success.50' : 'warning.50',
                  '&:hover': { bgcolor: isCompleted ? 'success.100' : 'warning.100' }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {item.title}
                  </Typography>
                  {item.description && (
                    <Typography variant="caption" color="text.secondary">
                      {item.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getItemTypeLabel(item.item_type)} 
                    size="small" 
                    variant="outlined" 
                  />
                </TableCell>
                <TableCell>
                  {formatResponse(item, response)}
                </TableCell>
                <TableCell>
                  {response?.notes ? (
                    <Typography variant="body2">
                      {response.notes}
                    </Typography>
                  ) : (
                    <Typography color="text.secondary" fontStyle="italic">
                      No notes
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {item.is_required ? (
                    <Chip label="Required" size="small" color="error" />
                  ) : (
                    <Chip label="Optional" size="small" variant="outlined" />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCardReview = () => (
    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
      {checklist.items.map((item) => {
        const response = responses[item.id];
        const isCompleted = checklistResponseCountsAsDone(response);
        
        return (
          <Card 
            key={item.id} 
            sx={{ 
              mb: 2,
              border: isCompleted ? '2px solid #4caf50' : '2px solid #ff9800'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {item.title}
                  </Typography>
                  {item.description && (
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip 
                    label={getItemTypeLabel(item.item_type)} 
                    size="small" 
                    variant="outlined" 
                  />
                  {item.is_required ? (
                    <Chip label="Required" size="small" color="error" />
                  ) : (
                    <Chip label="Optional" size="small" variant="outlined" />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Response:
                </Typography>
                {formatResponse(item, response)}
              </Box>

              {response?.notes && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {response.notes}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  // Render items for a specific instance
  const renderInstanceItems = (instance: ChecklistInstance) => {
    const instResponses = instanceResponses?.[instance.id] || {};
    
    return (
      <Box>
        {checklist.items.map((item) => {
          const response = instResponses[item.id];
          const isCompleted = checklistResponseCountsAsDone(response);
          
          return (
            <Card 
              key={item.id} 
              sx={{ 
                mb: 2,
                border: isCompleted ? '2px solid #4caf50' : '2px solid #ff9800'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.title}
                    </Typography>
                    {item.description && (
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={getItemTypeLabel(item.item_type)} 
                      size="small" 
                      variant="outlined" 
                    />
                    {item.is_required ? (
                      <Chip label="Required" size="small" color="error" />
                    ) : (
                      <Chip label="Optional" size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Response:
                  </Typography>
                  {formatResponse(item, response)}
                </Box>

                {response?.notes && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Notes:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {response.notes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  // Render overall instance summary
  const renderInstanceSummary = () => {
    if (!instances || instances.length === 0) return null;

    return (
      <Card sx={{ mb: 3, bgcolor: 'info.50', border: '2px solid', borderColor: 'info.main' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="info" />
            Overall Summary - {instances.length} Instance{instances.length !== 1 ? 's' : ''}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
            {instances.map((instance, idx) => {
              const instStatus = getInstanceCompletionStatus(instance.id);
              const isInstanceComplete = instStatus.completedRequired === instStatus.required;
              
              return (
                <Box 
                  key={instance.id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isInstanceComplete ? 'success.main' : 'warning.main'
                  }}
                >
                  {isInstanceComplete ? 
                    <CheckIcon color="success" fontSize="large" /> : 
                    <WarningIcon color="warning" fontSize="large" />
                  }
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Instance {idx + 1}: {instance.instance_name}
                    </Typography>
                    {instance.instance_identifier && (
                      <Typography variant="body2" color="text.secondary">
                        ID: {instance.instance_identifier}
                      </Typography>
                    )}
                    {instance.location_details && (
                      <Typography variant="caption" color="text.secondary">
                        {instance.location_details}
                      </Typography>
                    )}
                  </Box>
                  <Chip 
                    label={`${instStatus.completed}/${instStatus.total} Complete`}
                    color={isInstanceComplete ? 'success' : 'warning'}
                    size="medium"
                  />
                  <Typography variant="h6" color={isInstanceComplete ? 'success.main' : 'warning.main'}>
                    {instStatus.percentage}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" gutterBottom>
          Review & Submit Checklist
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please review your responses before submitting. You can go back to edit any item if needed.
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Completion Summary */}
        <Alert 
          severity={isComplete ? "success" : "warning"} 
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Completion Status
          </Typography>
          <Typography variant="body2">
            {status.completed} of {status.total} items completed ({status.percentage}%)
          </Typography>
          <Typography variant="body2">
            {status.completedRequired} of {status.required} required items completed ({status.requiredPercentage}%)
          </Typography>
          {isComplete && (
            <Typography variant="body2" fontWeight="bold">
              ✓ All required items completed - ready to submit!
            </Typography>
          )}
          {missingCannotCompleteReason && (
            <Typography variant="body2" fontWeight="bold">
              Enter a reason for each item marked Cannot Complete before submitting. That item is not scored as done.
            </Typography>
          )}
        </Alert>

        {/* Multi-Instance Summary */}
        {renderInstanceSummary()}

        {/* Review Content */}
        <Box>
          <Typography variant="h6" gutterBottom>
            {instances && instances.length > 0 ? 'Detailed Responses by Instance' : 'Checklist Responses'}
          </Typography>
          
          {instances && instances.length > 0 ? (
            // Multi-instance view with accordions
            <Box>
              {instances.map((instance, idx) => (
                <Accordion key={instance.id} defaultExpanded={idx === 0}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'grey.50' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <Typography variant="h6">
                        Instance {idx + 1}: {instance.instance_name}
                      </Typography>
                      {instance.instance_identifier && (
                        <Chip 
                          label={instance.instance_identifier} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Chip 
                        label={`${getInstanceCompletionStatus(instance.id).completed}/${getInstanceCompletionStatus(instance.id).total}`}
                        color={getInstanceCompletionStatus(instance.id).completedRequired === getInstanceCompletionStatus(instance.id).required ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {renderInstanceItems(instance)}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            // Single instance view (original behavior)
            <>
              {checklist.items.some(item => 
                item.item_type === 'file_upload' || 
                item.item_type === 'photo' || 
                item.item_type === 'location'
              ) ? (
                renderCardReview()
              ) : (
                renderTableReview()
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Go Back & Edit
        </Button>
        <Button 
          variant="contained" 
          onClick={onComplete}
          disabled={!isComplete}
          startIcon={<CheckIcon />}
        >
          Submit Checklist
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistReviewDialog; 